/**
 * Ticket WK3: reine Logik des Nachrichten-Kanals.
 *
 * Bewusst ohne Netz, ohne Datenbank und ohne React: Abfragen je Gemeinde,
 * das Lesen der RSS-Antwort, die dreistufige Nachprüfung eines Treffers, der
 * Artikelschlüssel gegen Wiederholungsfunde und die Eingrenzung des
 * Abfragekreises sind damit vollständig prüfbar.
 *
 * Grundregel: Ein Fund meldet, er entscheidet nicht. Und: lieber ein Fund
 * weniger als ein falscher.
 */

import { normalizeMunicipalityName } from "@/lib/wahlkalender";

/* --------------------------------------------------- Abfragen je Gemeinde */

export type PressQueryTarget = {
  name: string;
  district: string;
  /** Geltendes Wahldatum als ISO-Tag, für die Jahreszahl in Abfrage 2. */
  electionDate: string | null;
  /** Mehrdeutiger Gemeindename: der Landkreis gehört in jede Abfrage. */
  ambiguous: boolean;
};

/**
 * Vier Abfragen je Gemeinde. Der Gemeindename ist Pflichtbestandteil und steht
 * in Anführungszeichen als zusammenhängender Ausdruck. Bei mehrdeutigen Namen
 * kommt der Landkreis als zweites Merkmal hinzu.
 */
export function buildPressQueries(target: PressQueryTarget): string[] {
  const name = target.name.trim();
  if (!name) return [];
  const phrase = `"${name}"`;
  const district = target.ambiguous ? ` "${cleanDistrict(target.district)}"` : "";
  const year = target.electionDate ? target.electionDate.slice(0, 4) : null;
  const queries = [
    `${phrase}${district} "Bürgermeisterwahl"`,
    year ? `${phrase}${district} "Bürgermeisterwahl" ${year}` : null,
    `${phrase}${district} Kandidatur Bürgermeister`,
    `${phrase}${district} ("neuer Bürgermeister" OR "tritt nicht mehr an")`,
  ].filter((value): value is string => value !== null);
  return queries;
}

function cleanDistrict(district: string): string {
  return district.replace(/^(Landkreis|Stadtkreis)\s+/iu, "").trim();
}

/** Adresse des Nachrichten-Kanals zu einer Abfrage. */
export function pressFeedUrl(query: string): string {
  const params = new URLSearchParams({ q: query, hl: "de", gl: "DE", ceid: "DE:de" });
  return `https://news.google.com/rss/search?${params.toString()}`;
}

/* ------------------------------------------------ Lesen der RSS-Antwort */

export type PressFeedItem = {
  title: string;
  link: string;
  guid: string | null;
  publishedAt: string | null;
  sourceName: string;
  /** Adresse der Quelle aus dem Quellfeld des Kanals, wenn vorhanden. */
  sourceUrl: string | null;
};

function tag(block: string, name: string): string | null {
  const match = new RegExp(`<${name}[^>]*>([\\s\\S]*?)</${name}>`, "iu").exec(block);
  return match ? decodeXml(match[1]!.trim()) : null;
}

function decodeXml(value: string): string {
  return value
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gu, "$1")
    .replace(/&lt;/gu, "<")
    .replace(/&gt;/gu, ">")
    .replace(/&quot;/gu, '"')
    .replace(/&#39;/gu, "'")
    .replace(/&apos;/gu, "'")
    .replace(/&nbsp;/gu, " ")
    .replace(/&amp;/gu, "&")
    .trim();
}

/**
 * Die Antwort des Kanals liefert Titel, Verweis, Kennung, Datum und den
 * Quellnamen. Eine Zusammenfassung liefert sie nicht – das Beschreibungsfeld
 * wiederholt nur den Titel. Titel der Form „Überschrift - Quelle" werden
 * getrennt, wenn kein eigenes Quellfeld vorliegt.
 */
export function parsePressFeed(xml: string): PressFeedItem[] {
  const items: PressFeedItem[] = [];
  const blocks = xml.match(/<item>[\s\S]*?<\/item>/giu) ?? [];
  for (const block of blocks) {
    const rawTitle = tag(block, "title");
    const link = tag(block, "link");
    if (!rawTitle || !link) continue;
    const explicitSource = tag(block, "source");
    const sourceBlock = /<source[^>]*url="([^"]+)"[^>]*>/iu.exec(block);
    const sourceUrl = sourceBlock ? decodeXml(sourceBlock[1]!.trim()) : null;
    let title = rawTitle;
    let sourceName = explicitSource ?? "";
    if (!sourceName) {
      const split = /^([\s\S]+) - ([^-]+)$/u.exec(rawTitle);
      if (split) {
        title = split[1]!.trim();
        sourceName = split[2]!.trim();
      }
    }
    const pubDate = tag(block, "pubDate");
    const parsed = pubDate ? new Date(pubDate) : null;
    items.push({
      title,
      link,
      guid: tag(block, "guid"),
      publishedAt: parsed && !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : null,
      sourceName,
      sourceUrl,
    });
  }
  return items;
}

/** Artikelschlüssel gegen Wiederholungsfunde: Kennung, sonst der Verweis. */
export function pressArticleKey(item: { guid?: string | null; link: string }): string {
  const raw = (item.guid ?? "").trim() || item.link.trim();
  return raw.slice(0, 400);
}

/* ------------------------------------------ Dreistufige Nachprüfung */

export type PressMatchStage = 1 | 2 | 3;

export type PressMatchTarget = {
  name: string;
  district: string;
  ambiguous: boolean;
};

export type PressMatchInput = {
  title: string;
  sourceName: string;
  link: string;
  /** Text der Artikelseite, nur für die dritte Stufe. */
  articleText?: string | null;
};

/** Vergleichsform eines Fließtextes, damit „Bad Saulgau" auch in einer Adresse greift. */
function haystack(value: string): string {
  return ` ${normalizeMunicipalityName(value.replace(/[^\p{L}\p{N}]+/gu, " "))} `;
}

function contains(text: string, needle: string): boolean {
  const wanted = normalizeMunicipalityName(needle).trim();
  if (!wanted) return false;
  return haystack(text).includes(` ${wanted} `);
}

/**
 * Nimmt den Treffer an, sobald eine Stufe greift:
 * 1. Gemeindename im Titel.
 * 2. Gemeindename im Quellnamen oder in der Adresse des Artikels.
 * 3. Gemeindename im Text der Artikelseite (kostet einen zusätzlichen Abruf).
 *
 * Bei mehrdeutigen Namen muss der Landkreis in derselben Stufenfolge ebenfalls
 * nachweisbar sein. Greift keine Stufe, wird der Treffer verworfen (null).
 */
export function pressMatchStage(
  target: PressMatchTarget,
  input: PressMatchInput,
): PressMatchStage | null {
  const stages: Array<{ stage: PressMatchStage; text: string }> = [
    { stage: 1, text: input.title },
    { stage: 2, text: `${input.sourceName} ${input.link}` },
    { stage: 3, text: input.articleText ?? "" },
  ];

  let nameStage: PressMatchStage | null = null;
  for (const entry of stages) {
    if (contains(entry.text, target.name)) {
      nameStage = entry.stage;
      break;
    }
  }
  if (nameStage === null) return null;
  if (!target.ambiguous) return nameStage;

  const districtName = cleanDistrict(target.district);
  let districtStage: PressMatchStage | null = null;
  for (const entry of stages) {
    if (contains(entry.text, districtName)) {
      districtStage = entry.stage;
      break;
    }
  }
  if (districtStage === null) return null;
  return Math.max(nameStage, districtStage) as PressMatchStage;
}

/**
 * Ticket PR2: Verweise des Nachrichtenkanals (news.google.com) führen auf eine
 * Seite von Google, nicht auf den Artikel. Ihr Abruf liefert keinen
 * Artikeltext, kostet aber eine Anfrage bei Google und bringt die Sperre des
 * Kanals näher. Solche Verweise bekommen keine dritte Stufe.
 */
export function isFetchableArticleLink(link: string): boolean {
  let host: string;
  try {
    host = new URL(link.trim()).hostname.toLowerCase();
  } catch {
    return false;
  }
  return host !== "news.google.com" && !host.endsWith(".news.google.com");
}

/** Braucht dieser Treffer die dritte Stufe, also einen zusätzlichen Abruf? */
export function needsArticleFetch(target: PressMatchTarget, input: PressMatchInput): boolean {
  return pressMatchStage(target, { ...input, articleText: null }) === null;
}

/* ------------------------------ Ticket WK8: wöchentlicher Lauf in zwei Teilen */

export const PRESS_SCOPE_MONTHS = 18;
/** Teil B: Gemeinden je Woche aus dem Rest (etwa alle acht Monate einmal dran). */
export const PRESS_FOLLOWUP_BATCH = 100;
/** Obergrenze zusätzlicher Artikelabrufe je Aufruf (dritte Stufe). */
export const PRESS_ARTICLE_FETCH_LIMIT = 150;
/** Zeitbudget eines Aufrufs; der Lauf wird danach fortgesetzt. */
export const PRESS_TIME_BUDGET_MS = 120_000;
/**
 * Ticket PR2: Pause zwischen zwei Abfragen beim Nachrichtenkanal. Schnelle
 * Folgen von Abfragen führen dort zur Sperre (429/503).
 */
export const PRESS_QUERY_PAUSE_MS = 2_000;

/** Ticket PR2: das Ergebnis eines Aufrufs, so weit die Meldung es braucht. */
export type PressRunSummary = {
  status: "offen" | "beendet";
  municipalitiesChecked: number;
  progress: number;
  total: number;
  found: number;
  rateLimited: boolean;
  feedFailures: number;
};

/**
 * Ticket PR2: Meldung nach „Presse abrufen". Eine Sperre des Kanals wird
 * ausdrücklich genannt, statt wie ein Lauf ohne Funde auszusehen.
 */
export function describePressRun(report: PressRunSummary): {
  tone: "success" | "warning";
  text: string;
} {
  const counts = `${report.municipalitiesChecked} Gemeinden abgefragt, ${report.found} Funde`;
  if (report.rateLimited) {
    const open = Math.max(report.total - report.progress, 0);
    return {
      tone: "warning",
      text: `Presse: Google News sperrt den Abruf gerade. ${counts}, ${open} von ${report.total} Gemeinden noch offen. Der Zeitplan setzt den Lauf später fort.`,
    };
  }
  // Ticket WK11: eine abgeschlossene Woche wird ausdrücklich gemeldet,
  // statt wie ein stiller Leerlauf auszusehen.
  const tail =
    report.status === "beendet"
      ? report.municipalitiesChecked === 0
        ? "Diese Woche ist bereits abgeschlossen; kein neuer Lauf."
        : "Lauf beendet."
      : "Lauf läuft weiter.";
  if (report.feedFailures > 0) {
    return {
      tone: "warning",
      text: `Presse: ${counts} (${tail}) ${report.feedFailures} Abfragen sind fehlgeschlagen.`,
    };
  }
  return { tone: "success", text: `Presse: ${counts} (${tail}).` };
}

/**
 * Ticket WK11: Höchstalter eines Fundes. Ältere Artikel melden nichts Neues –
 * sie erzeugen keinen Fall. Ein Fund ohne erkennbares Datum gilt als nicht
 * datierbar und wird ebenfalls verworfen. Die Grenze steht nur hier.
 */
export const PRESS_MAX_AGE_MONTHS = 6;

/** Ticket WK11: Ist der Fund jung genug, um vorgelegt zu werden? */
export function isFreshPressItem(publishedAt: string | null | undefined, today: string): boolean {
  if (!publishedAt) return false;
  const parsed = new Date(publishedAt);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) >= monthsAhead(today, -PRESS_MAX_AGE_MONTHS);
}

/**
 * Die Gemeinden stehen ausschließlich in Deutschland – nur deutsche
 * Presseseiten erzeugen einen Fall. Maßgeblich ist die Quelladresse aus dem
 * Quellfeld des Kanals; der Verweis selbst ist eine Umleitung des Kanals und
 * sagt nichts über die Herkunft. Fehlt die Quelladresse, gilt der Verweis.
 * Zulässig sind nur Adressen, deren Host auf „.de" endet.
 */
export function isGermanPressItem(item: { sourceUrl?: string | null; link: string }): boolean {
  const candidate = (item.sourceUrl ?? "").trim() || item.link.trim();
  if (!candidate) return false;
  let host: string;
  try {
    host = new URL(candidate).hostname.toLowerCase();
  } catch {
    return false;
  }
  return host === "de" || host.endsWith(".de");
}

/**
 * Ticket WK8, Teil B: zwei enger gefasste Abfragen für die Nachlese. Keine
 * Jahreszahl und kein Wahltermin-Begriff – bei einer Wahl in sechs Jahren
 * trifft das nichts. Gesucht wird das vorzeitige Ende eines Amtes; „Bürgermeister"
 * bindet die zweite Abfrage an das Amt statt an irgendeine Nachfolge im Ort.
 */
export function buildPressFollowupQueries(target: PressQueryTarget): string[] {
  const name = target.name.trim();
  if (!name) return [];
  const phrase = `"${name}"`;
  const district = target.ambiguous ? ` "${cleanDistrict(target.district)}"` : "";
  return [
    `${phrase}${district} ("Rücktritt" OR "Amtsniederlegung")`,
    `${phrase}${district} ("Nachfolge" OR "Amtsnachfolge") Bürgermeister`,
  ];
}

function monthsAhead(iso: string, months: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, date.getUTCDate()),
  );
  return target.toISOString().slice(0, 10);
}

/** Gehört die Gemeinde in den dringenden Kreis (überfällig oder 18 Monate)? */
export function isInPressScope(electionDate: string | null, today: string): boolean {
  if (!electionDate) return false;
  return electionDate <= monthsAhead(today, PRESS_SCOPE_MONTHS);
}

/** Montag der Woche zu einem ISO-Tag – die Kennung einer Laufwoche. */
export function pressWeekOf(today: string): string {
  const date = new Date(`${today}T00:00:00Z`);
  const weekday = date.getUTCDay();
  const shift = weekday === 0 ? -6 : 1 - weekday;
  date.setUTCDate(date.getUTCDate() + shift);
  return date.toISOString().slice(0, 10);
}

export type PressCandidate = {
  id: string;
  name: string;
  district: string;
  electionDate: string | null;
  /** Ticket WK11: Wahltermin menschlich bestätigt – dann wird nicht gesucht. */
  confirmed?: boolean;
};

export type PressSelection<T extends PressCandidate> = {
  /** Teil A, der dringende Kreis: jede Woche vollständig. */
  urgent: T[];
  /** Teil B, die rotierende Nachlese aus dem Rest. */
  followup: T[];
  /** Neuer Stand der Rotation nach einem beendeten Lauf. */
  nextCursor: string | null;
};

/**
 * Ticket WK8: Teil A ist jede Woche vollständig dran und wird bei jedem Lauf
 * neu berechnet. Teil B nimmt einhundert Gemeinden aus dem Rest, dort weiter,
 * wo die Rotation stehengeblieben ist. Der dringende Kreis kommt in Teil B nie
 * vor – er läuft ohnehin jede Woche.
 *
 * Ticket WK11: Gemeinden mit bestätigtem Wahltermin bleiben in beiden Teilen
 * außen vor – dort ist nichts mehr zu finden.
 */
export function selectWeeklyPressTargets<T extends PressCandidate>(
  rows: T[],
  today: string,
  options: { rotationCursor?: string | null; batch?: number } = {},
): PressSelection<T> {
  const batch = options.batch ?? PRESS_FOLLOWUP_BATCH;
  const open = rows.filter((row) => row.confirmed !== true);
  const urgent = open
    .filter((row) => isInPressScope(row.electionDate, today))
    .sort((a, b) => (a.electionDate ?? "").localeCompare(b.electionDate ?? "") || a.id.localeCompare(b.id));
  const rest = open
    .filter((row) => !isInPressScope(row.electionDate, today))
    .sort((a, b) => a.id.localeCompare(b.id));

  if (rest.length === 0 || batch <= 0) return { urgent, followup: [], nextCursor: null };

  const cursor = options.rotationCursor ?? null;
  const found = cursor === null ? -1 : rest.findIndex((row) => row.id > cursor);
  const startIndex = found === -1 ? 0 : found;
  const followup: T[] = [];
  for (let i = 0; i < Math.min(batch, rest.length); i += 1) {
    followup.push(rest[(startIndex + i) % rest.length]!);
  }
  return { urgent, followup, nextCursor: followup[followup.length - 1]?.id ?? null };
}


/** Mehrdeutige Gemeindenamen im Bestand: Name mehr als einmal vergeben. */
export function ambiguousNames(rows: Array<{ name: string }>): Set<string> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = normalizeMunicipalityName(row.name);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const result = new Set<string>();
  for (const [key, count] of counts) if (count > 1) result.add(key);
  return result;
}

export function isAmbiguousName(name: string, ambiguous: Set<string>): boolean {
  return ambiguous.has(normalizeMunicipalityName(name));
}
